import { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Checkbox,
    Divider,
    Group,
    Modal,
    ScrollArea,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';

export interface VendorFormValues {
    name: string;
    provider_abbreviation: string;
    organization_id: string;
    domain: string;
    website_url: string;
    open_trust_url: string;
    trust_center_url: string;
    logo: string;
    favicon: string;
    product_name: string;
    slug: string;
    is_default: boolean;
    metadata: string;
}

const EMPTY: VendorFormValues = {
    name: '',
    provider_abbreviation: '',
    organization_id: '',
    domain: '',
    website_url: '',
    open_trust_url: '',
    trust_center_url: '',
    logo: '',
    favicon: '',
    product_name: '',
    slug: '',
    is_default: false,
    metadata: '',
};

interface VendorFormModalProps {
    opened: boolean;
    mode: 'create' | 'edit';
    /** Prefill for edit mode; ignored on create. */
    initial?: Partial<VendorFormValues>;
    submitting: boolean;
    onClose: () => void;
    /** Only the fields relevant to the mode are sent. */
    onSubmit: (values: VendorFormValues) => void;
}

/** True when `value` is a syntactically valid absolute http(s) URL. */
const isValidUrl = (value: string): boolean => {
    try {
        const u = new URL(value);
        return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
        return false;
    }
};

/**
 * Mirror the server slug rule (catalog.dto.ts): lowercase url-safe tokens
 * (a-z, 0-9) joined by single `-._/` separators, no leading/trailing/doubled
 * separator. Validated with a single-class charset test plus fixed boundary
 * checks — deliberately NOT one `(X+)*`-shaped regex, which trips ReDoS
 * scanners (each check here is linear-time).
 */
const isValidSlug = (slug: string): boolean =>
    /^[a-z0-9._/-]+$/.test(slug) && // allowed characters only
    !/^[-._/]/.test(slug) && // no leading separator
    !/[-._/]$/.test(slug) && // no trailing separator
    !/[-._/]{2,}/.test(slug); // no consecutive separators

/**
 * Shared add/edit form for a Company Catalog entry (admin-only). Presentational:
 * the parent owns the mutation, invalidation, and notifications.
 *
 * BOTH modes expose the FULL record — company identity (provider / organization
 * id), branding (logo / favicon), and product fields (name / slug / is-default /
 * metadata) — so an admin can populate everything up front on create and fix a
 * mis-provisioned vendor (e.g. wrong provider) on edit, without a code change or
 * direct DB access (VI-783). The only difference is the identity tuple: on create
 * it's optional (the server mints a MANUAL provider / uuid org / `default` slug
 * for any part left blank); on edit it's required (the row already has one).
 * Validations mirror the server: required name, URL-shaped link fields, and the
 * slug token rule (checked in both modes whenever a slug is supplied).
 */
export const VendorFormModal = ({
    opened,
    mode,
    initial,
    submitting,
    onClose,
    onSubmit,
}: VendorFormModalProps): React.JSX.Element => {
    const [values, setValues] = useState<VendorFormValues>(EMPTY);
    const isEdit = mode === 'edit';

    // Re-seed the form each time the modal opens. `initial` is built inline by
    // the parent (new reference each render), so key the effect on its serialized
    // VALUE — a background refetch on window focus then can't wipe an in-progress edit.
    const initialKey = JSON.stringify(initial ?? {});
    useEffect(() => {
        if (opened) {
            setValues({ ...EMPTY, ...initial });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, initialKey]);

    const setText = (key: keyof VendorFormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
        // Capture synchronously: React nulls `e.currentTarget` after the handler
        // returns, so reading it inside the updater would throw.
        const value = e.currentTarget.value;
        setValues((v) => ({ ...v, [key]: value }));
    };

    const errors = useMemo(() => {
        const e: Partial<Record<keyof VendorFormValues, string>> = {};
        if (values.name.trim() === '') e.name = 'Name is required';
        const urlField = (key: keyof VendorFormValues): void => {
            const val = (values[key] as string).trim();
            if (val !== '' && !isValidUrl(val)) e[key] = 'Must be a valid http(s) URL';
        };
        urlField('website_url');
        urlField('open_trust_url');
        urlField('trust_center_url');
        urlField('logo');
        urlField('favicon');
        // Identity tuple is required only on edit (the row already has one); on
        // create the server mints any blank part. The slug FORMAT is enforced in
        // both modes whenever a value is supplied.
        if (isEdit) {
            if (values.provider_abbreviation.trim() === '')
                e.provider_abbreviation = 'Provider is required';
            if (values.organization_id.trim() === '')
                e.organization_id = 'Organization ID is required';
        }
        const slug = values.slug.trim();
        if (isEdit && slug === '') e.slug = 'Slug is required';
        else if (slug !== '' && !isValidSlug(slug))
            e.slug = 'Lowercase url-safe tokens only (a-z, 0-9, - . _ /)';
        return e;
    }, [values, isEdit]);

    const hasErrors = Object.keys(errors).length > 0;

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            centered
            size="lg"
            title={mode === 'create' ? 'Add company catalog entry' : 'Edit company catalog entry'}
            data-testid="VendorFormModal"
        >
            <ScrollArea.Autosize mah="70vh">
                <Stack gap="sm" pr="md">
                    <TextInput
                        label="Name"
                        placeholder="Acme Corp"
                        required
                        value={values.name}
                        onChange={setText('name')}
                        error={errors.name}
                        data-testid="VendorFormName"
                    />

                    <Group grow align="flex-start">
                        <TextInput
                            label="Provider"
                            placeholder={isEdit ? 'MANUAL' : 'MANUAL (auto if blank)'}
                            description={
                                isEdit ? undefined : 'Leave blank to mint a MANUAL identity.'
                            }
                            required={isEdit}
                            value={values.provider_abbreviation}
                            onChange={setText('provider_abbreviation')}
                            error={errors.provider_abbreviation}
                            data-testid="VendorFormProvider"
                        />
                        <TextInput
                            label="Organization ID"
                            description={isEdit ? undefined : 'Leave blank to mint a random id.'}
                            required={isEdit}
                            value={values.organization_id}
                            onChange={setText('organization_id')}
                            error={errors.organization_id}
                            data-testid="VendorFormOrganizationId"
                        />
                    </Group>

                    <TextInput
                        label="Domain"
                        placeholder="acme.com"
                        value={values.domain}
                        onChange={setText('domain')}
                        data-testid="VendorFormDomain"
                    />
                    <TextInput
                        label="Website URL"
                        placeholder="https://acme.com"
                        value={values.website_url}
                        onChange={setText('website_url')}
                        error={errors.website_url}
                        data-testid="VendorFormWebsiteUrl"
                    />
                    <TextInput
                        label="Trust center URL"
                        placeholder="https://trust.acme.com"
                        value={values.trust_center_url}
                        onChange={setText('trust_center_url')}
                        error={errors.trust_center_url}
                        data-testid="VendorFormTrustCenterUrl"
                    />
                    <TextInput
                        label="Open trust URL"
                        placeholder="https://acme.com/trust"
                        value={values.open_trust_url}
                        onChange={setText('open_trust_url')}
                        error={errors.open_trust_url}
                        data-testid="VendorFormOpenTrustUrl"
                    />

                    <TextInput
                        label="Logo URL"
                        placeholder="https://acme.com/logo.png"
                        value={values.logo}
                        onChange={setText('logo')}
                        error={errors.logo}
                        data-testid="VendorFormLogo"
                    />
                    <TextInput
                        label="Favicon URL"
                        placeholder="https://acme.com/favicon.ico"
                        value={values.favicon}
                        onChange={setText('favicon')}
                        error={errors.favicon}
                        data-testid="VendorFormFavicon"
                    />

                    <Divider
                        my="xs"
                        label={
                            <Text size="sm" fw={600}>
                                Product
                            </Text>
                        }
                        labelPosition="left"
                    />

                    <TextInput
                        label="Product name"
                        placeholder="Acme Platform"
                        value={values.product_name}
                        onChange={setText('product_name')}
                        data-testid="VendorFormProductName"
                    />
                    <TextInput
                        label="Slug"
                        placeholder={isEdit ? 'default' : 'default (auto if blank)'}
                        description={isEdit ? undefined : 'Leave blank to use the `default` slug.'}
                        required={isEdit}
                        value={values.slug}
                        onChange={setText('slug')}
                        error={errors.slug}
                        data-testid="VendorFormSlug"
                    />
                    <TextInput
                        label="Metadata"
                        description="Provider-specific stable id (optional)."
                        value={values.metadata}
                        onChange={setText('metadata')}
                        data-testid="VendorFormMetadata"
                    />
                    <Checkbox
                        label="Is default product"
                        checked={values.is_default}
                        onChange={(e) => {
                            const checked = e.currentTarget.checked;
                            setValues((v) => ({ ...v, is_default: checked }));
                        }}
                        data-testid="VendorFormIsDefault"
                    />

                    <Group justify="flex-end" mt="sm">
                        <Button
                            variant="subtle"
                            color="gray"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                onSubmit({ ...values, name: values.name.trim() });
                            }}
                            loading={submitting}
                            disabled={hasErrors}
                            data-testid="VendorFormSubmit"
                        >
                            {mode === 'create' ? 'Add' : 'Save'}
                        </Button>
                    </Group>
                </Stack>
            </ScrollArea.Autosize>
        </Modal>
    );
};
